import { Component, forwardRef, Inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AccountGroup } from '@galaxy/account-group';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { ConfigInterface, Order, Status } from '@vendasta/sales-orders';
import { User } from '@vendasta/store';
import { WorkOrderPersona } from '@vendasta/work-order';
import { combineLatest, Observable, of, Subscription, throwError } from 'rxjs';
import { catchError, delay, map, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { OrderStoreService } from '../../core/orders.service';
import { OrderPermissions, PAGE_ORDER_CONFIG_TOKEN, PageOrderConfig } from '../../core/tokens';
import { Badge, badgeWithOrderStatus } from '../../core/status';
import { ORDER_FORM_FILE_UPLOAD_URL } from '../../utils';
import { EditOrderComponent } from '../edit-order/edit-order.component';
import { ViewOrderComponent } from '../view-order/view-order.component';
import { FeatureFlagService } from '@galaxy/partner';
import { Features } from '../../core/features';

const adminEditableStatuses: Status[] = [Status.SUBMITTED, Status.APPROVED, Status.DRAFTED];
const salesperonEditableStatuses: Status[] = [Status.DRAFTED];

@Component({
  selector: 'orders-view-or-edit-order',
  templateUrl: './view-or-edit-order.component.html',
  styleUrls: ['./view-or-edit-order.component.scss'],
  standalone: false,
})
export class ViewOrEditOrderComponent implements OnInit, OnDestroy {
  @Input() hideNotesSections?: boolean;
  @Input() hideTagsSection?: boolean;
  @Input() hideAttachmentsSection?: boolean;
  @Input() hideOrderFormSaveButton?: boolean;
  @Input() workOrderPersona?: WorkOrderPersona = WorkOrderPersona.SALES;
  @Input() fileUploadUrl?: string = ORDER_FORM_FILE_UPLOAD_URL;

  business$: Observable<AccountGroup>;
  order$: Observable<Order>;
  canEdit$: Observable<boolean>;
  users$: Observable<User[]>;
  productIds$: Observable<string[]>;
  loading$: Observable<boolean>;
  unifiedOrderPageEnabled$!: Observable<boolean>;

  statusBadge: Badge;

  private readonly subscriptions: Subscription[] = [];

  @ViewChild(forwardRef(() => ViewOrderComponent)) viewOrderComponent: ViewOrderComponent;
  @ViewChild('editOrder') editOrderComponent: EditOrderComponent;

  constructor(
    public readonly orderService: OrderStoreService,
    @Inject(PAGE_ORDER_CONFIG_TOKEN) private readonly pageOrderConfig: PageOrderConfig,
    private readonly snackbarService: SnackbarService,
    private readonly featureFlagService: FeatureFlagService,
  ) {}

  ngOnInit(): void {
    this.business$ = this.orderService.business$.pipe(shareReplay(1));
    this.order$ = this.orderService.order$.pipe(
      catchError((error) => {
        this.snackbarService.openErrorSnack('An error occurred getting the order. Please try again');
        return throwError(error);
      }),
      tap((order: Order) => {
        this.editOrderComponent?.salesOrderForm?.refresh(order);
        this.statusBadge = badgeWithOrderStatus(order.status);
      }),
      shareReplay(1),
    );

    this.unifiedOrderPageEnabled$ = this.business$.pipe(
      switchMap((business) => {
        const partnerId = business?.externalIdentifiers?.partnerId;
        const accountGroupId = business?.accountGroupId;
        if (!partnerId || !accountGroupId) {
          return of(false);
        }
        return this.featureFlagService
          .batchGetStatus(partnerId, accountGroupId, [Features.PC_UNIFIED_ORDERS_PAGE])
          .pipe(map((res) => res[Features.PC_UNIFIED_ORDERS_PAGE]));
      }),
    );
    this.initViewMode();
    this.users$ = this.orderService.businessUsers$.pipe(shareReplay(1));
    this.productIds$ = this.orderService.productIds$.pipe(shareReplay(1));
    this.loading$ = combineLatest([this.order$, this.business$, this.users$, this.productIds$, this.canEdit$]).pipe(
      map(() => false),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  initViewMode(): void {
    this.canEdit$ = combineLatest([
      this.business$,
      this.order$,
      this.pageOrderConfig.orderPermissions$,
      this.orderService.orderConfig$,
      this.unifiedOrderPageEnabled$,
    ]).pipe(
      map(([business, order, orderPermissions, orderConfig, unifiedOrderPageEnabled]) =>
        isOrderEditable(order, business, orderPermissions, orderConfig, unifiedOrderPageEnabled),
      ),
    );
  }

  validateForm(): Observable<boolean> {
    if (this.viewOrderComponent) {
      return of(true);
    }
    return this.editOrderComponent?.validateForm() || of(false);
  }

  refreshOrderDetails(): void {
    this.orderService.reloadOrder();
  }

  saveOrderFormIfDirty(): Observable<boolean> {
    return this.editOrderComponent.saveOrderFormIfDirty();
  }

  preparePrintPage(): Observable<unknown> {
    return this.canEdit$.pipe(
      take(1),
      tap((isEditMode) => {
        // the edit mode (order in draft) doesn't have the components that need to be expanded
        if (!isEditMode) {
          this.viewOrderComponent.openDropdowns();
        }
      }),
      // give the dropdowns time to open before executing the print
      delay(750),
    );
  }
}

export function isOrderEditable(
  order: Order,
  business: AccountGroup,
  orderPermissions: OrderPermissions,
  orderConfig: ConfigInterface,
  unifiedOrderPageEnabled = false,
): boolean {
  // always show edit-component if the unified order page is enabled since we are moving towards a single modular component
  if (unifiedOrderPageEnabled) {
    return true;
  }

  if (!order || !business) {
    return false;
  }

  let editableStatuses = adminEditableStatuses;
  if (!orderPermissions.accessManageOrders && !orderConfig.workflowStepOptions.allowOrderFormEditing) {
    editableStatuses = salesperonEditableStatuses;
  }
  return editableStatuses.includes(order.status) && !business.deleted;
}
